import { useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { term, termShort, termHelp } from "@/lib/terms";
import { useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";
import { MoneyInput } from "@/components/MoneyInput";
import { BtwHint } from "@/components/btw/BtwHint";
import { BTW_AFTREK_OPTIONS, aftrekLabel } from "@/lib/btw";
import { ArrowLeft, ChevronDown, ChevronRight, Copy, Download, Save, Star } from "lucide-react";
import HelpButton from "@/components/HelpButton";
import { toast } from "sonner";

// Module-scope form-inputs. Voorheen stonden `NumField` en `MoneyField` binnen
// `ScenarioBuilder` gedefinieerd, waardoor React ze bij elke render als NIEUWE
// componenten zag en de <input> unmounted + remounted. Dat kostte de cursor
// tijdens het typen. Nu op module-niveau; de render-scoped `form/update/
// guardRate` refs komen via een lichte Context binnen zodat call-sites kort
// blijven en de component-identiteit stabiel is over renders.
const FormBindingContext = createContext(null);

function NumField({ k, label, hint, step = "any", guard = false }) {
  const { form, update, guardRate } = useContext(FormBindingContext);
  return (
    <div>
      <label className="stat-label">{label}</label>
      <input
        type="number"
        step={step}
        value={form[k]}
        onChange={(e) => update(k, e.target.value)}
        onBlur={guard ? (e) => guardRate(k, e.target.value, label) : undefined}
        data-testid={`sf-${k}`}
        className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
      />
      {hint && <div data-testid={`sf-${k}_hint`} className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function MoneyField({ k, label, hint }) {
  const { form, update } = useContext(FormBindingContext);
  return (
    <div>
      <label className="stat-label">{label}</label>
      <MoneyInput value={form[k]} onCommit={(n) => update(k, n)} testid={`sf-${k}`} />
      {hint && <div data-testid={`sf-${k}_hint`} className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

// Percentage-veld — user typt in procenten (bijv. 2 of 2,5), maar de waarde
// wordt intern (in form + naar backend) opgeslagen als decimaal (0,02 / 0,025).
// Bij lege input blijft er "" staan zodat de user het veld kan wissen zonder
// dat het als 0% wordt geïnterpreteerd. Zonder aparte guard-warning: 25 (voor
// 25%) is nu een normale input i.p.v. verdacht.
// Percentage-input voor plekken die niet in de FormBindingContext zitten
// (bijv. investeringsplan-rijen, dotaties). Werkt met een externe waarde +
// callback die de decimale value terugzet. Lege string blijft leeg.
function PctInput({ value, onChange, testid, placeholder, step = "any", className }) {
  const display = value === "" || value === null || value === undefined
    ? ""
    : (Number(value) * 100).toString().replace(/\.0+$/, "");
  const handle = (e) => {
    const v = e.target.value;
    if (v === "" || v == null) { onChange(""); return; }
    const n = Number(v.replace(",", "."));
    if (Number.isNaN(n)) { onChange(""); return; }
    onChange(Math.round((n / 100) * 1e6) / 1e6);
  };
  return (
    <div className="relative mt-1">
      <input
        type="number"
        step={step}
        value={display}
        onChange={handle}
        data-testid={testid}
        placeholder={placeholder}
        className={className || "w-full border-2 border-foreground bg-background px-3 py-2 pr-8 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono-num pointer-events-none">%</span>
    </div>
  );
}

function PercentField({ k, label, hint, step = "any" }) {
  const { form, update } = useContext(FormBindingContext);
  const raw = form[k];
  // Toon in %-en (decimaal × 100). Bewaar rauwe "" om edit-flow niet te breken.
  const display = raw === "" || raw === null || raw === undefined
    ? ""
    : (Number(raw) * 100).toString().replace(/\.0+$/, "");
  const onChange = (e) => {
    const v = e.target.value;
    if (v === "" || v == null) { update(k, ""); return; }
    const n = Number(v.replace(",", "."));
    if (Number.isNaN(n)) { update(k, ""); return; }
    // Store as decimal (percent ÷ 100). Round to 6 dp to avoid float noise.
    update(k, Math.round((n / 100) * 1e6) / 1e6);
  };
  return (
    <div>
      <label className="stat-label">{label}</label>
      <div className="relative mt-1">
        <input
          type="number"
          step={step}
          value={display}
          onChange={onChange}
          data-testid={`sf-${k}`}
          className="w-full border-2 border-foreground bg-background px-3 py-2 pr-8 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono-num pointer-events-none">%</span>
      </div>
      {hint && <div data-testid={`sf-${k}_hint`} className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}



const defaultForm = {
  name: "Basisscenario",
  kind: "base",
  horizon_years: 10,
  start_year: new Date().getFullYear(),
  initial_capex: 0,
  land_cost: 0,
  permits_cost: 0,
  annual_opex: 0,
  maintenance_pct_of_capex: 0,
  staff_cost_annual: 0,
  utilities_annual: 0,
  unit_count: 0,
  revenue_per_unit_annual: 0,
  occupancy_rate: 1,
  membership_revenue_annual: 0,
  event_revenue_annual: 0,
  loan_amount: 0,
  interest_rate: 0.05,
  loan_term_years: 15,
  loan_start_year: new Date().getFullYear(),
  loan_type: "annuity",
  equity_amount: 0,
  existing_debt_years: 15,
  show_npv_modules: false,
  tax_rate: 0.25,
  inflation_rate: 0.02,
  discount_rate: 0.08,
  interest_income_rate: 0.01,
  depreciation_years: 20,
  starting_cash: 50000,
  starting_cash_override: false,
  annual_provision_withdrawal: 0,
  annual_reserve_from_result: 0,
  annual_working_capital_change: 0,
  annual_short_term_debt_change: 0,
  annual_debtor_creditor_change: 0,
  annual_vat: 0,
  // NEW mental model
  use_last_historical_baseline: true,
  baseline_source: "historical",
  baseline_year: null,
  revenue_growth_rate: 0.02,
  opex_growth_rate: 0.03,
  other_income_growth_rate: 0.02,
  subpost_growth: {},
  scenario_future_investments: [],
  reserve_dotations: [],
  notes: "",
};



export default function ScenarioBuilder() {
  const t = useT();
  const { clubId, scenarioId } = useParams();
  const editing = Boolean(scenarioId);
  const nav = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [step, setStep] = useState(0);
  const [templateApplied, setTemplateApplied] = useState(false);
  const { user } = useAuth();
  const hasNpvAccess = user && (user.tier === "TRIAL" || user.tier === "PREMIUM" || user.role === "admin");

  const STEPS = [
    { key: "identity", label: t("Identiteit") },
    { key: "baseline", label: t("Basis & groei") },
    { key: "invplan", label: t("Investeringsplan") },
    { key: "reserves", label: t("Reserve-dotaties") },
    { key: "financing", label: t("Financiering & aannames") },
  ];

  useEffect(() => {
    if (editing) {
      api.get(`/scenarios/${scenarioId}`).then(({ data }) => setForm({ ...defaultForm, ...data }));
    }
  }, [scenarioId, editing]);

  // Load the club's reserves so the "funded from reservation" dropdown can show
  // available balances and validate amounts client-side before the server 402s.
  const [reserves, setReserves] = useState([]);
  useEffect(() => {
    if (!clubId) return;
    api.get(`/clubs/${clubId}/reserves`).then(({ data }) => setReserves(data || [])).catch(() => {});
  }, [clubId]);

  // Load the club's existing loans (used to show the "geen leningen ingevuld"
  // fallback-hint in de financiering-stap wanneer historische rente > 0 én er
  // nog geen gedetailleerde leningen zijn vastgelegd op club-niveau).
  const [existingLoans, setExistingLoans] = useState([]);
  useEffect(() => {
    if (!clubId) return;
    api.get(`/clubs/${clubId}/existing-loans`).then(({ data }) => setExistingLoans(data || [])).catch(() => {});
  }, [clubId]);

  // For new scenarios, pull the club's latest "template" values (inflation, discount, growth rates
  // and per-subpost growth) so the builder is pre-filled with the most-recently-used numbers.
  // Een expliciete club-standaard (default_subpost_growth) gaat vóór de laatst gebruikte subpost-percentages.
  const [club, setClub] = useState(null);
  useEffect(() => {
    if (!clubId) return;
    api.get(`/clubs/${clubId}`).then(({ data }) => {
      setClub(data);
      if (editing) return;
      const d = data?.scenario_defaults;
      const clubDefaults = data?.default_subpost_growth;
      const hasClubDefaults = Object.values(clubDefaults || {}).some((cat) => cat && Object.keys(cat).length > 0);
      if (!d && !hasClubDefaults) return;
      setForm((f) => {
        const merged = { ...f };
        const keys = [
          "inflation_rate", "tax_rate", "discount_rate",
          "revenue_growth_rate", "opex_growth_rate", "other_income_growth_rate",
        ];
        for (const k of keys) {
          if (d && d[k] !== undefined && d[k] !== null) merged[k] = d[k];
        }
        if (d?.subpost_growth && typeof d.subpost_growth === "object") {
          merged.subpost_growth = d.subpost_growth;
        }
        if (hasClubDefaults) {
          merged.subpost_growth = clubDefaults;
        }
        return merged;
      });
      setTemplateApplied(hasClubDefaults ? "club" : true);
    }).catch(() => {});
  }, [clubId, editing]);

  // Fetch historicals for the club to power the starting_cash auto-fill suggestion.
  // Track loading/error explicitly so a failed request isn't confused with "no history".
  const [historicals, setHistoricals] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [histStatus, setHistStatus] = useState("loading"); // loading | ok | error
  useEffect(() => {
    if (!clubId) return;
    setHistStatus("loading");
    api.get(`/clubs/${clubId}/historicals`)
      .then(({ data }) => { setHistoricals(data); setHistStatus("ok"); })
      .catch(() => setHistStatus("error"));
    api.get(`/clubs/${clubId}/budgets`)
      .then(({ data }) => setBudgets(data))
      .catch(() => setBudgets([]));
  }, [clubId]);

  // Suggested opening cash: the cash+bank balance of the year immediately before start_year,
  // otherwise the most recent historical row we have.
  const suggestedCash = useMemo(() => {
    if (!historicals || historicals.length === 0) return null;
    const startYear = Number(form.start_year) || new Date().getFullYear();
    const exact = historicals.find((h) => h.year === startYear - 1);
    if (exact && exact.cash_bank_balance !== undefined) {
      return { year: exact.year, value: Number(exact.cash_bank_balance) || 0 };
    }
    const sorted = [...historicals].sort((a, b) => b.year - a.year);
    const latest = sorted[0];
    if (latest) return { year: latest.year, value: Number(latest.cash_bank_balance) || 0 };
    return null;
  }, [historicals, form.start_year]);

  // When auto mode is active, keep starting_cash in sync with the suggested value.
  useEffect(() => {
    if (form.starting_cash_override) return;
    if (suggestedCash) {
      setForm((f) =>
        f.starting_cash === suggestedCash.value ? f : { ...f, starting_cash: suggestedCash.value }
      );
    }
  }, [suggestedCash, form.starting_cash_override]);

  // Auto-set start_year to (max historical year + 1) once on load for new scenarios,
  // so the historical-gap detector defaults to green for well-maintained dossiers.
  // Runs only once — and skipped entirely if the user has already touched the field.
  const startYearAutoSetRef = useRef(false);
  const startYearTouchedRef = useRef(false);
  useEffect(() => {
    if (editing) return;
    if (histStatus !== "ok") return;
    if (startYearAutoSetRef.current) return;
    if (startYearTouchedRef.current) return;
    if (!historicals || historicals.length === 0) return;
    const maxYear = Math.max(...historicals.map((h) => Number(h.year) || 0));
    if (!maxYear) return;
    setForm((f) => ({ ...f, start_year: maxYear + 1, loan_start_year: maxYear + 1 }));
    startYearAutoSetRef.current = true;
  }, [editing, histStatus, historicals]);

  const handleStartYearChange = (e) => {
    startYearTouchedRef.current = true;
    update("start_year", e.target.value);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Guard against the very common mistake of typing "2" when "0.02" (=2%) is
  // meant. Any rate-input above 1.0 (>100%) triggers a confirm dialog offering
  // to divide by 100 or accept the value as-is. Fires on blur (not each keystroke).
  const [rateWarning, setRateWarning] = useState(null); // {key, value, label}
  const guardRate = (key, rawValue, label) => {
    if (rawValue === "" || rawValue == null) return;
    const v = Number(rawValue);
    if (Number.isFinite(v) && v > 1) {
      setRateWarning({ key, value: v, label });
    }
  };
  const applyRateFix = () => {
    if (!rateWarning) return;
    update(rateWarning.key, rateWarning.value / 100);
    setRateWarning(null);
  };
  const acceptRateAsIs = () => setRateWarning(null);

  const submit = async () => {
    try {
      const payload = { ...form };
      // Coerce numeric-ish fields but preserve arrays, strings and nullable/optional fields.
      // NB: Number(null) === 0, so we must guard nulls explicitly — otherwise
      // nullable fields like `baseline_year` / `scenario_summary` end up as 0
      // and blow past pydantic's `ge=1900` guard on the server.
      const SKIP = new Set([
        "name", "kind", "notes", "scenario_future_investments", "loan_type",
        "baseline_source", "scenario_summary", "chart_descriptions",
        "subpost_growth", "reserve_dotations",
      ]);
      Object.keys(payload).forEach((k) => {
        if (SKIP.has(k)) return;
        if (Array.isArray(payload[k])) return;
        if (typeof payload[k] === "boolean") return;
        if (payload[k] === null || payload[k] === undefined || payload[k] === "") return;
        const n = Number(payload[k]);
        if (!Number.isNaN(n)) payload[k] = n;
      });
      // baseline_year is only meaningful when baseline_source === "budget"
      if (payload.baseline_source !== "budget") {
        payload.baseline_year = null;
      } else if (payload.baseline_year != null && payload.baseline_year !== "") {
        payload.baseline_year = Number(payload.baseline_year);
      }
      // Coerce inside each future investment row.
      payload.scenario_future_investments = (form.scenario_future_investments || []).map((it) => ({
        name: String(it.name || ""),
        amount: Number(it.amount) || 0,
        expected_subsidy: Number(it.expected_subsidy) || 0,
        money_year: it.money_year === "" || it.money_year == null ? null : Number(it.money_year),
        cost_inflation_rate: it.cost_inflation_rate === "" || it.cost_inflation_rate == null ? null : Number(it.cost_inflation_rate),
        start_year: Number(it.start_year) || 0,
        funded_from_reservation: Boolean(it.funded_from_reservation),
        reservation_name: it.funded_from_reservation ? (it.reservation_name || null) : null,
        depreciation_years: Number(it.depreciation_years) || 0,
        btw_aftrek: it.btw_aftrek || null,
        additional_revenue: Number(it.additional_revenue) || 0,
        additional_costs: Number(it.additional_costs) || 0,
        additional_revenue_growth: it.additional_revenue_growth === "" || it.additional_revenue_growth == null ? null : Number(it.additional_revenue_growth),
        additional_costs_growth: it.additional_costs_growth === "" || it.additional_costs_growth == null ? null : Number(it.additional_costs_growth),
        loan_amount: Number(it.loan_amount) || 0,
        loan_interest_rate: Number(it.loan_interest_rate) || 0.05,
        loan_term_years: Number(it.loan_term_years) || 15,
        loan_type: String(it.loan_type || "annuity"),
        discount_rate: it.discount_rate === "" || it.discount_rate == null ? null : Number(it.discount_rate),
      }));
      // Strip client-only `_lid` from reserve_dotations before sending. Pydantic
      // already ignores unknown fields, but keeping the wire-payload clean makes
      // the request logs easier to eyeball.
      payload.reserve_dotations = (form.reserve_dotations || []).map((d) => ({
        reserve_name: String(d.reserve_name || ""),
        amount_per_year: Number(d.amount_per_year) || 0,
        growth_rate: d.growth_rate === "" || d.growth_rate == null ? null : Number(d.growth_rate),
        start_year: d.start_year === "" || d.start_year == null ? null : Number(d.start_year),
        end_year: d.end_year === "" || d.end_year == null ? null : Number(d.end_year),
      }));
      const url = editing ? `/scenarios/${scenarioId}` : `/clubs/${clubId}/scenarios`;
      const method = editing ? "put" : "post";
      const { data } = await api[method](url, payload);
      toast.success(editing ? "Scenario updated" : "Scenario created");
      nav(`/clubs/${clubId}/scenarios/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Fresh object each render is fine here: NumField/MoneyField are module-
  // scope components so React never unmounts the <input>. The consumer just
  // re-renders when context changes, which is what we want.
  const formBinding = { form, update, guardRate };

  return (
    <FormBindingContext.Provider value={formBinding}>
    <AppLayout>
      <button onClick={() => nav(`/clubs/${clubId}`)} data-testid="back-club" className="text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> {t("Terug naar club")}
      </button>
      <div className="stat-label">{editing ? t("Bewerken van scenario") : t("Aanmaken van scenario")}</div>
      <h1 className="font-display font-black text-5xl uppercase tracking-tighter mt-1 mb-3">{t("Scenariobouwer")}</h1>

      {!editing && (
        <div
          data-testid="scenario-walkthrough-banner"
          className="mb-5 inline-flex items-start gap-3 border-2 border-foreground bg-yellow-100 dark:bg-yellow-900/30 px-4 py-3"
        >
          <span className="font-display uppercase font-black text-xs tracking-widest text-foreground/80 shrink-0 mt-0.5">
            {t("Tip")}
          </span>
          <span className="text-sm text-foreground">
            <span className="font-display uppercase font-bold">{t("Doorloop stap 01-05")}</span>
            {" — "}
            {t("werk elke stap af voor een compleet scenario. Je kunt tussentijds opslaan en later terugkomen.")}
          </span>
        </div>
      )}

      {templateApplied && !editing && (
        <div data-testid="template-applied-banner" className="mb-5 inline-flex items-center gap-2 border-2 border-foreground bg-primary/10 px-3 py-2">
          <span className="text-primary font-display uppercase font-bold text-xs tracking-widest">{t("Sjabloon toegepast")}</span>
          <span className="text-xs text-muted-foreground">{t("Inflatie, disconteringsvoet en groeivoeten overgenomen van laatst opgeslagen scenario.")}{templateApplied === "club" && ` ${t("Inflatie per subpost: club-standaard.")}`}</span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {STEPS.map((s, i) => (
          <button key={s.key} onClick={() => setStep(i)} data-testid={`step-${s.key}`}
            className={`px-3 py-2 font-display uppercase font-bold text-sm border-2 border-foreground ${
              step === i ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
            }`}>
            <span className="font-mono-num mr-2 opacity-70">{String(i + 1).padStart(2, "0")}</span>{s.label}
          </button>
        ))}
      </div>

      <div className="bg-card border-2 border-foreground p-6 heavy-shadow">
        {STEPS[step]?.key === "identity" && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="stat-label">{t("Scenarionaam")}</label>
              <input value={form.name} onChange={(e) => update("name", e.target.value)}
                data-testid="sf-name"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="stat-label">{t("Type")}</label>
              <select value={form.kind} onChange={(e) => update("kind", e.target.value)}
                data-testid="sf-kind"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="best">{t("Beste")}</option>
                <option value="base">{t("Basis")}</option>
                <option value="worst">{t("Slechtste")}</option>
              </select>
            </div>
            <NumField k="horizon_years" label={t("Tijdshorizon (jaren)")} step="1" hint={t("Probeer 5, 10 of 20 jaar")} />
            <div>
              <label className="stat-label">{t("Startjaar")}</label>
              <input
                type="number"
                step="1"
                value={form.start_year}
                onChange={handleStartYearChange}
                data-testid="sf-start_year"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <div data-testid="sf-start_year_hint" className="text-xs text-muted-foreground mt-1">
                {historicals && historicals.length > 0
                  ? t("Automatisch gezet op laatst historisch jaar + 1")
                  : t("Standaard huidig jaar")}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="stat-label">{t("Notities")}</label>
              <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)}
                data-testid="sf-notes"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px]" />
            </div>
          </div>
        )}
        {STEPS[step]?.key === "baseline" && (
          <BaselineStep t={t} form={form} update={update} historicals={historicals} budgets={budgets} histStatus={histStatus} guardRate={guardRate} clubId={clubId} scenarioId={scenarioId} club={club} onClubSaved={setClub} />
        )}
        {STEPS[step]?.key === "invplan" && (
          <FutureInvestmentsStep
            t={t}
            form={form}
            setForm={setForm}
            startYear={Number(form.start_year) || new Date().getFullYear()}
            scenarioDiscount={Number(form.discount_rate) || 0.08}
            scenarioInflation={Number(form.inflation_rate) || 0.02}
            horizonYears={Number(form.horizon_years) || 10}
            reserves={reserves}
            hasNpvAccess={hasNpvAccess}
            club={club}
          />
        )}
        {STEPS[step]?.key === "reserves" && (
          <ReserveDotationsStep
            t={t}
            form={form}
            setForm={setForm}
            startYear={Number(form.start_year) || new Date().getFullYear()}
            horizonYears={Number(form.horizon_years) || 10}
            scenarioInflation={Number(form.inflation_rate) || 0.02}
            reserves={reserves}
          />
        )}
        {STEPS[step]?.key === "financing" && (
          <div className="space-y-6">
            {/* Info-banner: bestaande leningen staan op club-niveau */}
            <div className="border-2 border-foreground bg-muted/30 p-4" data-testid="existing-loans-info-banner">
              <div className="stat-label mb-1">{t("Bestaande leningen")}</div>
              <p className="text-sm text-muted-foreground">
                {t("Bestaande leningen worden beheerd op club-niveau via de tab 'Bestaande leningen'. Ze worden automatisch meegenomen in dit scenario met exacte amortisatie per lening.")}
              </p>
              {(() => {
                const last = (historicals || []).slice().sort((a, b) => b.year - a.year)[0];
                const v = last?.interest_paid || 0;
                if (v && (!existingLoans || existingLoans.length === 0)) {
                  return (
                    <p className="text-xs text-amber-700 mt-2" data-testid="existing-loans-fallback-hint">
                      {t("Let op: er zijn geen leningen op club-niveau vastgelegd. Het model valt terug op de rente uit het laatste boekjaar (€{v}) en bouwt die lineair af.").replace("{v}", Number(v).toLocaleString("nl-NL", { maximumFractionDigits: 0 }))}
                    </p>
                  );
                }
                return null;
              })()}
            </div>

            {/* Extra financiering — optionele scenario-lening bovenop bestaande */}
            <div className="border-2 border-foreground p-4" data-testid="new-loan-card">
              <div className="stat-label mb-1">{t("Extra financiering (optioneel)")}</div>
              <div className="text-xs text-muted-foreground mb-3">
                {t("Voeg een extra lening toe op scenario-niveau, bovenop leningen binnen investeringsplannen en de bestaande club-leningen.")}
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <MoneyField k="loan_amount" label={t("Leningsbedrag (€)")} />
                <PercentField k="interest_rate" label={t("Rentepercentage")} />
                <NumField k="loan_term_years" label={t("Leningstermijn (jaren)")} step="1" />
                <NumField k="loan_start_year" label={t("Jaar van uitkering")} step="1" hint={t("Standaard = startjaar. Zet gelijk aan de investering die je met deze lening financiert.")} />
                <div>
                  <label className="stat-label">{t("Aflossingsstructuur")}</label>
                  <select value={form.loan_type} onChange={(e) => update("loan_type", e.target.value)}
                    data-testid="sf-loan_type"
                    className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="annuity">{t("Annuïteit (gelijke jaarlast)")}</option>
                    <option value="linear">{t("Lineair (gelijke aflossing)")}</option>
                  </select>
                  <div className="text-xs text-muted-foreground mt-1">
                    {form.loan_type === "linear"
                      ? t("Elk jaar hetzelfde aflossingsbedrag; rente daalt met de restschuld.")
                      : t("Elk jaar dezelfde totale betaling; aflossing stijgt, rente daalt.")}
                  </div>
                </div>
                <MoneyField k="equity_amount" label={t("Eigen inbreng (€)")} />
              </div>
            </div>

            {/* Algemene aannames (voorheen aparte stap Macro) */}
            <div className="border-2 border-foreground p-4" data-testid="macro-card">
              <div className="stat-label mb-1">{t("Algemene aannames")}</div>
              <div className="text-xs text-muted-foreground mb-3">
                {t("Inflatie, belasting en rente gelden voor het hele scenario. De groei per post stel je in bij stap 02.")}
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <PercentField k="inflation_rate" label={t("Inflatiepercentage")} />
                <PercentField k="tax_rate" label={t("Vennootschapsbelasting")} hint={t("Verenigingen/stichtingen zonder VpB-plicht: 0.")} />
                {hasNpvAccess && (
                  <PercentField k="discount_rate" label={term("wacc")} hint={t("Rekenrente voor de netto contante waarde (NPV).")} />
                )}
                <PercentField k="interest_income_rate" label={t("Rentebaten (over gem. liquiditeit)")} />
              </div>
            </div>
          </div>
        )}
        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              data-testid="prev-step" className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold disabled:opacity-40">
              {t("Vorige")}
            </button>
            <button onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))} disabled={step === STEPS.length - 1}
              data-testid="next-step" className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold disabled:opacity-40">
              {t("Volgende")}
            </button>
          </div>
          <button onClick={submit} data-testid="save-scenario"
            className="btn-brutal bg-primary text-primary-foreground px-5 py-3 font-display uppercase font-black tracking-wider inline-flex items-center gap-2">
            <Save className="w-5 h-5" /> {editing ? t("Bijwerken") : t("Opslaan & berekenen")}
          </button>
        </div>
      </div>

      {rateWarning && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={acceptRateAsIs}
          data-testid="rate-warning-backdrop"
        >
          <div
            className="max-w-md w-full bg-card border-4 border-destructive p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="rate-warning-dialog"
            role="dialog"
            aria-modal="true"
          >
            <div className="stat-label text-destructive">{t("Percentage-check")}</div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
              {t("Weet je zeker dat je {value} bedoelt?").replace("{value}", String(rateWarning.value))}
            </h2>
            <p className="text-sm text-muted-foreground mt-3">
              {t("Je vulde")} <b className="font-mono-num">{rateWarning.value}</b> {t("in bij")} <b>{rateWarning.label}</b>.
              {" "}{t("Dat betekent")} <b className="font-mono-num">{(rateWarning.value * 100).toFixed(0)}%</b> {t("per jaar.")}
            </p>
            <p className="text-sm mt-2">
              {t("Waarschijnlijk bedoelde je")} <b className="font-mono-num text-primary">{rateWarning.value / 100}</b> ({(rateWarning.value).toFixed(2)}% {t("per jaar")}).
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={applyRateFix}
                data-testid="rate-warning-fix"
                className="btn-brutal bg-primary text-primary-foreground px-4 py-3 font-display uppercase font-bold text-sm text-left"
              >
                {t("Ja, corrigeer naar")} {rateWarning.value / 100} ({(rateWarning.value).toFixed(2)}%)
              </button>
              <button
                type="button"
                onClick={acceptRateAsIs}
                data-testid="rate-warning-accept"
                className="btn-brutal bg-background text-foreground border-2 border-foreground px-4 py-3 font-display uppercase font-bold text-sm text-left"
              >
                {t("Nee, ik bedoel echt")} {(rateWarning.value * 100).toFixed(0)}% {t("per jaar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
    </FormBindingContext.Provider>
  );
}

function StartingCashField({ t, form, update, suggested, status }) {
  const override = Boolean(form.starting_cash_override);
  const hint = (() => {
    if (override) return t("Overschrijf handmatig");
    if (status === "loading") return t("Laden…");
    if (status === "error") return t("Kon historische cijfers niet ophalen — controleer handmatig");
    if (suggested) return t("Automatisch gevuld uit kas + bank saldo per {year}").replace("{year}", String(suggested.year));
    return t("Geen historisch saldo beschikbaar — vul handmatig");
  })();
  const hintColor = (() => {
    if (override) return "text-muted-foreground";
    if (status === "error") return "text-destructive font-semibold";
    return "text-primary font-semibold";
  })();
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="stat-label">{t("1. Liquide positie op 1 januari (€)")}</label>
        <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] font-bold uppercase tracking-widest">
          <input
            type="checkbox"
            checked={override}
            onChange={(e) => update("starting_cash_override", e.target.checked)}
            data-testid="sf-starting_cash_override"
            className="w-4 h-4 accent-blue-600"
          />
          {t("Overschrijf handmatig")}
        </label>
      </div>
      <MoneyInput
        value={form.starting_cash}
        readOnly={!override}
        onCommit={(n) => update("starting_cash", n)}
        testid="sf-starting_cash"
        className={`w-full border-2 border-foreground px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary ${override ? "bg-background" : "bg-muted"}`}
      />
      <div data-testid="sf-starting_cash_hint" className={`text-xs mt-1 ${hintColor}`}>{hint}</div>
    </div>
  );
}

function BaselineStep({ t, form, update, historicals, budgets, histStatus, guardRate, clubId, scenarioId, club, onClubSaved }) {
  const useLast = Boolean(form.use_last_historical_baseline);
  const src = form.baseline_source || "historical";
  const last = (() => {
    if (!historicals || historicals.length === 0) return null;
    return [...historicals].sort((a, b) => b.year - a.year)[0];
  })();
  const availableBudgets = (budgets || []).slice().sort((a, b) => b.year - a.year);
  const pickedBudget = availableBudgets.find((b) => b.year === Number(form.baseline_year));
  const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(v) || 0);
  return (
    <div>
      <div className="stat-label">{t("Basis & groei")}</div>
      <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">{t("Extrapoleer vanaf laatste boekjaar")}</h2>
      <label className={`flex items-start gap-3 p-4 border-2 cursor-pointer transition-colors mb-6 ${useLast ? "border-primary bg-primary/5" : "border-foreground bg-background hover:bg-muted"}`}>
        <input type="checkbox" checked={useLast} onChange={(e) => update("use_last_historical_baseline", e.target.checked)}
          data-testid="sf-use_last_historical_baseline" className="mt-1 w-5 h-5 accent-blue-600" />
        <div>
          <div className="font-display font-bold uppercase tracking-wide">{t("Gebruik laatste historisch jaar als basis")}</div>
          <div className="text-sm text-muted-foreground mt-1">{t("Omzet, bedrijfskosten en overige inkomsten worden geëxtrapoleerd vanaf het meest recente boekjaar in de historische cijfers. De afschrijvingsstaat van bestaande activa wordt automatisch meegenomen.")}</div>
        </div>
      </label>

      {useLast && availableBudgets.length > 0 && (
        <div className="mb-6 border-2 border-foreground p-4" data-testid="scenario-baseline-source">
          <div className="stat-label mb-2">{t("Startpunt van de projectie")}</div>
          <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
            {t("Kies of de projectie start vanaf de laatste jaarrekening óf een specifieke begroting. Nuttig in de voorjaarslus wanneer je begroting {y} klaar hebt en de scenario's daarop wilt bouwen.").replace("{y}", String(availableBudgets[0]?.year || ""))}
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            <button type="button"
              onClick={() => { update("baseline_source", "historical"); update("baseline_year", null); }}
              data-testid="scenario-baseline-source-historical"
              className={`text-left border-2 p-3 transition ${src === "historical" ? "border-foreground bg-primary/10" : "border-foreground/30 hover:border-foreground"}`}
            >
              <div className="font-display font-black uppercase tracking-tight text-sm">{t("Laatste jaarrekening")}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {last ? `${last.year} · ${fmt(last.revenue)} ${t("omzet")}` : t("Nog geen jaarrekening geïmporteerd.")}
              </div>
            </button>
            <button type="button"
              onClick={() => {
                update("baseline_source", "budget");
                if (!pickedBudget && availableBudgets[0]) update("baseline_year", availableBudgets[0].year);
              }}
              data-testid="scenario-baseline-source-budget"
              className={`text-left border-2 p-3 transition ${src === "budget" ? "border-foreground bg-primary/10" : "border-foreground/30 hover:border-foreground"}`}
            >
              <div className="font-display font-black uppercase tracking-tight text-sm">{t("Begroting")}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {availableBudgets.length === 1
                  ? `${availableBudgets[0].year} · ${fmt(availableBudgets[0].revenue)} ${t("omzet")}`
                  : t("Kies hieronder welk begrotingsjaar.")}
              </div>
            </button>
          </div>
          {src === "budget" && availableBudgets.length > 1 && (
            <div className="mt-3">
              <div className="stat-label mb-1">{t("Begrotingsjaar")}</div>
              <select
                value={form.baseline_year || ""}
                onChange={(e) => update("baseline_year", parseInt(e.target.value, 10))}
                data-testid="scenario-baseline-year"
                className="border-2 border-foreground bg-background px-3 py-2 font-mono-num"
              >
                {availableBudgets.map((b) => <option key={b.id} value={b.year}>{b.year}</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {useLast && (
        <div className="border-2 border-foreground p-4 bg-muted/40 mb-6" data-testid="baseline-preview">
          <div className="stat-label mb-2">{t("Basisjaar")}</div>
          {histStatus === "loading" && <div className="text-sm text-muted-foreground">{t("Laden…")}</div>}
          {histStatus === "error" && <div className="text-sm text-destructive font-semibold">{t("Kon historische cijfers niet ophalen — controleer handmatig")}</div>}
          {histStatus === "ok" && !last && <div className="text-sm text-destructive font-semibold">{t("Geen historische cijfers gevonden — importeer eerst een boekjaar.")}</div>}
          {last && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="stat-label">{t("Jaar")}</div><div className="font-mono-num font-bold text-lg">{last.year}</div></div>
              <div><div className="stat-label">{t("Omzet")}</div><div className="font-mono-num font-bold text-lg">{fmt(last.revenue)}</div></div>
              <div><div className="stat-label">{t("Bedr. kosten")}</div><div className="font-mono-num font-bold text-lg">{fmt(last.operating_costs)}</div></div>
              <div><div className="stat-label">{t("Overige ink.")}</div><div className="font-mono-num font-bold text-lg">{fmt(last.other_income)}</div></div>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <PercentField k="revenue_growth_rate" label={t("Groei omzet per jaar")} />
        <PercentField k="opex_growth_rate" label={t("Groei bedrijfskosten per jaar")} />
        <PercentField k="other_income_growth_rate" label={t("Groei overige inkomsten per jaar")} />
      </div>

      <p className="text-xs text-muted-foreground mt-5 max-w-2xl">
        {t("Groeivoeten worden per jaar toegepast op de basiswaarden. Als 'Gebruik laatste historisch jaar' uit staat, worden de handmatige velden uit stappen Bedr. kosten en Omzet gebruikt en geldt de inflatievoet uit stap 05 (Financiering & aannames).")}
      </p>

      {useLast && last && (last.revenue_breakdown?.length || last.opex_breakdown?.length) > 0 && (
        <SubpostInflationSection t={t} form={form} update={update} last={last} clubId={clubId} scenarioId={scenarioId} club={club} onClubSaved={onClubSaved} />
      )}
    </div>
  );
}

function CopyGrowthFromScenario({ t, clubId, scenarioId, last, onApply }) {
  const [sources, setSources] = useState(null);
  useEffect(() => {
    if (!clubId) return;
    api.get(`/clubs/${clubId}/scenarios`).then(({ data }) => {
      const hasRates = (s) => Object.values(s.subpost_growth || {}).some((cat) => cat && Object.keys(cat).length > 0);
      setSources((data || []).filter((s) => s.id !== scenarioId && hasRates(s)));
    }).catch(() => setSources([]));
  }, [clubId, scenarioId]);
  if (!sources || sources.length === 0) return null;

  const apply = (id) => {
    const src = sources.find((s) => s.id === id);
    if (!src) return;
    const next = {};
    let n = 0;
    for (const [cat, key] of [["revenue", "revenue_breakdown"], ["opex", "opex_breakdown"], ["other_income", "other_income_breakdown"]]) {
      const names = (last[key] || []).map((it) => it.name);
      const srcCat = src.subpost_growth?.[cat] || {};
      next[cat] = Object.fromEntries(names.filter((nm) => srcCat[nm] !== undefined && srcCat[nm] !== null).map((nm) => [nm, Number(srcCat[nm])]));
      n += Object.keys(next[cat]).length;
    }
    onApply(next);
    toast.success(t("Inflatie per subpost overgenomen uit {name} ({n} posten).").replace("{name}", src.name).replace("{n}", n));
  };

  return (
    <label className="inline-flex items-center gap-2 text-xs" data-testid="subpost-copy-wrap">
      <Copy className="w-3.5 h-3.5" />
      <select defaultValue="" onChange={(e) => { apply(e.target.value); e.target.value = ""; }} data-testid="subpost-copy-select"
        className="border-2 border-foreground bg-background px-2 py-1 text-xs font-display uppercase font-bold focus:outline-none focus:ring-2 focus:ring-primary">
        <option value="" disabled>{t("Overnemen uit scenario…")}</option>
        {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </label>
  );
}

function ClubDefaultGrowthButtons({ t, clubId, club, last, current, onApply, onClubSaved }) {
  const [saving, setSaving] = useState(false);
  const defaults = club?.default_subpost_growth || {};
  const hasDefaults = Object.values(defaults).some((cat) => cat && Object.keys(cat).length > 0);
  const canEdit = !!club && club.shared_role !== "viewer" && !club.is_locked;
  const groups = [["revenue", "revenue_breakdown"], ["opex", "opex_breakdown"], ["other_income", "other_income_breakdown"]];
  const existing = (cat, key) => new Set((last[key] || []).map((it) => it.name));

  const applyDefaults = () => {
    const next = {};
    let n = 0;
    for (const [cat, key] of groups) {
      const names = existing(cat, key);
      next[cat] = Object.fromEntries(Object.entries(defaults[cat] || {}).filter(([nm, v]) => names.has(nm) && v !== null && v !== undefined));
      n += Object.keys(next[cat]).length;
    }
    onApply(next);
    toast.success(t("Club-standaard overgenomen ({n} posten).").replace("{n}", n));
  };
  const saveAsDefault = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [cat, key] of groups) {
        const names = existing(cat, key);
        payload[cat] = Object.fromEntries(Object.entries(current?.[cat] || {}).filter(([nm, v]) => names.has(nm) && v !== null && v !== undefined));
      }
      const { data } = await api.patch(`/clubs/${clubId}/subpost-growth`, { subpost_growth: payload });
      onClubSaved?.(data);
      const n = Object.values(data.default_subpost_growth || {}).reduce((a, cat) => a + Object.keys(cat || {}).length, 0);
      toast.success(t("Opgeslagen als club-standaard ({n} posten). Nieuwe scenario's starten hiermee.").replace("{n}", n));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <>
      <button type="button" onClick={applyDefaults} disabled={!hasDefaults} data-testid="subpost-apply-club-default"
        title={hasDefaults ? t("Vul de club-standaard in (vervangt de huidige percentages)") : t("Deze club heeft nog geen standaard inflatie per subpost")}
        className="btn-brutal bg-background text-foreground px-2.5 py-1 font-display uppercase font-bold text-[11px] inline-flex items-center gap-1 disabled:opacity-40">
        <Download className="w-3.5 h-3.5" /> {t("Club-standaard overnemen")}
      </button>
      {canEdit && (
        <button type="button" onClick={saveAsDefault} disabled={saving} data-testid="subpost-save-club-default"
          title={t("Maak van deze percentages de standaard voor nieuwe scenario's van deze club")}
          className="btn-brutal bg-secondary text-secondary-foreground px-2.5 py-1 font-display uppercase font-bold text-[11px] inline-flex items-center gap-1 disabled:opacity-50">
          <Star className="w-3.5 h-3.5" /> {t("Opslaan als club-standaard")}
        </button>
      )}
    </>
  );
}

function SubpostInflationSection({ t, form, update, last, clubId, scenarioId, club, onClubSaved }) {
  const cfg = form.subpost_growth || {};
  // Opslag als decimaal (0,03), weergave en invoer in % — net als de andere percentagevelden.
  const toPct = (v) => (v === "" || v === null || v === undefined ? "" : (Math.round(Number(v) * 1e6) / 1e4).toString());
  const setSub = (cat, name, val) => {
    const next = { ...(form.subpost_growth || {}) };
    const catObj = { ...(next[cat] || {}) };
    if (val === "" || val === null || val === undefined) {
      delete catObj[name];
    } else {
      const num = Number(String(val).replace(",", "."));
      if (Number.isNaN(num)) return;
      catObj[name] = Math.round((num / 100) * 1e6) / 1e6;
    }
    next[cat] = catObj;
    update("subpost_growth", next);
  };
  const groups = [
    ["revenue", t("Inkomsten"), last.revenue_breakdown || []],
    ["opex", t("Kosten"), last.opex_breakdown || []],
  ];
  const rowsPresent = groups.some(([, , items]) => (items || []).length > 0);
  const filled = Object.values(cfg).reduce((n, cat) => n + Object.keys(cat || {}).length, 0);
  const [open, setOpen] = useState(filled > 0);
  if (!rowsPresent) return null;
  if (!open) {
    return (
      <div className="mt-6 border-2 border-foreground p-4 bg-muted/40 flex items-center justify-between gap-3 flex-wrap" data-testid="subpost-inflation-section">
        <div>
          <div className="stat-label">{t("Detail · optioneel")}</div>
          <h3 className="font-display font-black text-lg uppercase tracking-tight">{t("Inflatie per subpost")}</h3>
          <p className="text-xs text-muted-foreground">{t("Standaard volgt elke subpost de groeivoet van zijn categorie. Klap uit om per subpost een eigen percentage te zetten.")}</p>
        </div>
        <button type="button" onClick={() => setOpen(true)} data-testid="subpost-inflation-expand"
          className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-xs">{t("Uitklappen")}</button>
      </div>
    );
  }
  return (
    <div className="mt-6 border-2 border-foreground p-4 bg-muted/40" data-testid="subpost-inflation-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="stat-label">{t("Detail · optioneel")}</div>
          <h3 className="font-display font-black text-lg uppercase tracking-tight mb-1">{t("Inflatie per subpost")}</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ClubDefaultGrowthButtons t={t} clubId={clubId} club={club} last={last} current={form.subpost_growth} onApply={(next) => update("subpost_growth", next)} onClubSaved={onClubSaved} />
          <CopyGrowthFromScenario t={t} clubId={clubId} scenarioId={scenarioId} last={last} onApply={(next) => update("subpost_growth", next)} />
          <button type="button" onClick={() => setOpen(false)} data-testid="subpost-inflation-collapse"
            className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-xs">{t("Inklappen")}</button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
        {t("Standaard: valt terug op inflatie / groeivoet van de categorie")}
      </p>
      <div className="grid md:grid-cols-3 gap-4">
        {groups.map(([cat, label, items]) => (
          <div key={cat} className="border-2 border-foreground/20 p-3" data-testid={`subpost-inflation-${cat}`}>
            <div className="stat-label mb-2">{label}</div>
            {(items || []).length === 0 && (
              <div className="text-xs text-muted-foreground italic">{t("Geen subposten")}</div>
            )}
            {(items || []).map((it) => (
              <div key={it.name} className="flex items-center gap-2 py-1">
                <span className="flex-1 truncate text-sm font-mono-num">{it.name}</span>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    placeholder="—"
                    value={toPct(cfg[cat]?.[it.name])}
                    onChange={(e) => setSub(cat, it.name, e.target.value)}
                    data-testid={`sf-subpost-${cat}-${it.name}`}
                    className="w-24 border-2 border-foreground bg-background px-2 py-1 pr-6 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground font-mono-num text-xs pointer-events-none">%</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Business case = extra opbrengsten óf een besparing (negatieve extra kosten).
function hasBusinessCase(plan) {
  return Number(plan.additional_revenue) > 0 || Number(plan.additional_costs) < 0;
}

function computePlanKpi(plan, opts) {
  const grossAmount = Number(plan.amount) || 0;
  const subsidy = Math.max(0, Number(plan.expected_subsidy) || 0);
  // Netto = bruto − subsidie. Subsidie dekt een deel van de investering
  // en verlaagt zowel de kasuitstroom als de basis voor lening/NPV/payback.
  const amount = Math.max(0, grossAmount - subsidy);
  if (amount <= 0) return null;
  const horizon = Math.max(1, Number(opts.horizon) || 10);
  const inflation = Number(opts.inflation) || 0;
  const funded = Boolean(plan.funded_from_reservation);
  const loan = funded ? 0 : (Number(plan.loan_amount) || 0);
  const loanRate = Number(plan.loan_interest_rate) || 0;
  const loanTerm = Math.max(1, Number(plan.loan_term_years) || 15);
  const loanType = plan.loan_type || "annuity";
  const revG = plan.additional_revenue_growth === "" || plan.additional_revenue_growth == null
    ? inflation : Number(plan.additional_revenue_growth);
  const costG = plan.additional_costs_growth === "" || plan.additional_costs_growth == null
    ? inflation : Number(plan.additional_costs_growth);
  const addRev = Number(plan.additional_revenue) || 0;
  const addCost = Number(plan.additional_costs) || 0;
  const discount = plan.discount_rate === "" || plan.discount_rate == null
    ? (Number(opts.discount) || 0.08) : Number(plan.discount_rate);
  const usingPlanDiscount = !(plan.discount_rate === "" || plan.discount_rate == null);
  const annuity = loan > 0 && loanRate > 0
    ? (loan * loanRate * Math.pow(1 + loanRate, loanTerm)) / (Math.pow(1 + loanRate, loanTerm) - 1)
    : (loan > 0 ? loan / loanTerm : 0);
  const cf = [];
  // Y0: equity outflow (amount - loan); reservation-funded → full amount outflow
  cf.push(-(amount - loan));
  let bal = loan;
  for (let k = 1; k <= horizon; k++) {
    const rev = addRev * Math.pow(1 + revG, k - 1); // additional_result starts at start_year (k=1 = start_year+1? — matches backend which grows from start)
    const cost = addCost * Math.pow(1 + costG, k - 1);
    let ls = 0;
    if (bal > 0 && k <= loanTerm) {
      const interest = bal * loanRate;
      let principal;
      if (loanType === "linear") principal = Math.min(bal, loan / loanTerm);
      else principal = Math.min(bal, Math.max(0, annuity - interest));
      ls = interest + principal;
      bal = Math.max(0, bal - principal);
    }
    cf.push(rev - cost - ls);
  }
  let npv = 0;
  for (let i = 0; i < cf.length; i++) npv += cf[i] / Math.pow(1 + discount, i);
  let cum = 0, payback = null;
  for (let i = 0; i < cf.length; i++) {
    const prev = cum;
    cum += cf[i];
    if (prev < 0 && cum >= 0) {
      const frac = cf[i] !== 0 ? (-prev) / cf[i] : 0;
      payback = (i - 1) + frac;
      break;
    }
  }
  // Discounted payback (using cash flows discounted at `discount`).
  let dcum = 0, dpayback = null;
  for (let i = 0; i < cf.length; i++) {
    const prev = dcum;
    const dv = cf[i] / Math.pow(1 + discount, i);
    dcum += dv;
    if (prev < 0 && dcum >= 0) {
      const frac = dv !== 0 ? (-prev) / dv : 0;
      dpayback = (i - 1) + frac;
      break;
    }
  }
  // IRR via bisection on [-0.99, 5]. Returns null when no sign change on cumulative or unsolvable.
  const npvAt = (r) => {
    let s = 0;
    for (let i = 0; i < cf.length; i++) s += cf[i] / Math.pow(1 + r, i);
    return s;
  };
  let irr = null;
  const totalNeg = cf.some((v) => v < 0);
  const totalPos = cf.some((v) => v > 0);
  if (totalNeg && totalPos) {
    let lo = -0.99, hi = 5.0;
    let fLo = npvAt(lo), fHi = npvAt(hi);
    if (fLo * fHi < 0) {
      for (let i = 0; i < 60; i++) {
        const mid = (lo + hi) / 2;
        const fMid = npvAt(mid);
        if (Math.abs(fMid) < 1e-4) { irr = mid; break; }
        if (fLo * fMid < 0) { hi = mid; fHi = fMid; } else { lo = mid; fLo = fMid; }
      }
      if (irr === null) irr = (lo + hi) / 2;
    }
  }
  return {
    npv, payback, dpayback, irr, discount, usingPlanDiscount,
    y0Cash: cf[0],
    totalCashOverHorizon: cf.reduce((s, v) => s + v, 0),
    horizon,
    amountInvested: amount,
    npvPerEuro: amount > 0 ? npv / amount : null,
  };
}

function FutureInvestmentsStep({ t, form, setForm, startYear, scenarioDiscount, scenarioInflation, horizonYears, reserves = [], hasNpvAccess = true, club = null }) {
  const items = form.scenario_future_investments || [];
  // Toggle state lives on the scenario itself so it's remembered across sessions.
  // STANDARD-tier users don't have NPV access → force-hide regardless of stored preference.
  const showNpv = hasNpvAccess && form.show_npv_modules !== false;
  const setShowNpv = (updater) => setForm((f) => {
    const cur = f.show_npv_modules !== false;
    const next = typeof updater === "function" ? updater(cur) : Boolean(updater);
    return { ...f, show_npv_modules: next };
  });
  const add = () => setForm((f) => ({
    ...f,
    scenario_future_investments: [
      ...(f.scenario_future_investments || []),
      { _lid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())), name: "", amount: 0, start_year: startYear, funded_from_reservation: false, depreciation_years: 10, additional_revenue: 0, additional_costs: 0, loan_amount: 0, loan_interest_rate: 0.05, loan_term_years: 15, loan_type: "annuity" },
    ],
  }));
  const remove = (idx) => setForm((f) => ({
    ...f,
    scenario_future_investments: (f.scenario_future_investments || []).filter((_, i) => i !== idx),
  }));
  const patch = (idx, k, v) => setForm((f) => ({
    ...f,
    scenario_future_investments: (f.scenario_future_investments || []).map((it, i) => (i === idx ? { ...it, [k]: v } : it)),
  }));
  // Geavanceerde velden per investering ingeklapt, tenzij er al waarden in staan.
  const [adv, setAdv] = useState({});
  const hasAdvValues = (it) => Boolean(Number(it.expected_subsidy) || it.money_year || (it.cost_inflation_rate !== "" && it.cost_inflation_rate != null)
    || Number(it.additional_revenue) || Number(it.additional_costs) || (it.additional_revenue_growth !== "" && it.additional_revenue_growth != null)
    || (it.additional_costs_growth !== "" && it.additional_costs_growth != null) || (it.discount_rate !== "" && it.discount_rate != null));
  const isAdvOpen = (it, idx) => adv[it._lid || idx] ?? hasAdvValues(it);
  const toggleAdv = (it, idx) => { const next = !isAdvOpen(it, idx); setAdv((a) => ({ ...a, [it._lid || idx]: next })); };
  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <div>
          <div className="stat-label">{t("Investeringsplan")}</div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Toekomstige investeringen")}</h2>
          <div className="mt-1"><BtwHint club={club} testid="invplan-btw-hint" /></div>
        </div>
        {hasNpvAccess && (
        <button
          type="button"
          onClick={() => setShowNpv((v) => !v)}
          data-testid="invplan-toggle-npv"
          aria-pressed={showNpv}
          className={`btn-brutal px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-3 border-2 border-foreground ${
            showNpv ? "bg-primary text-primary-foreground" : "bg-background text-foreground"
          }`}
          title={t("Toon of verberg de rendementsvergelijking (netto contante waarde) en de kerncijfers per investering")}
        >
          <span
            className={`inline-block w-8 h-4 border-2 border-foreground relative ${showNpv ? "bg-background" : "bg-foreground"}`}
            aria-hidden="true"
          >
            <span
              className={`absolute top-0 w-3 h-3 border-2 border-foreground transition-all ${
                showNpv ? "left-4 bg-primary" : "left-0 bg-background"
              }`}
              style={{ transform: "translate(-1px, -1px)" }}
            />
          </span>
          {t("Rendementscijfers (NPV)")} · {showNpv ? t("AAN") : t("UIT")}
        </button>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        {t("Voeg één of meer geplande investeringen toe. Bij financiering uit een bestemmingsreserve komt de investering ten laste van de reserve: wel een cash-uitstroom, maar geen capex-activering en dus geen afschrijving. Anders wordt de investering geactiveerd en lineair afgeschreven over de opgegeven looptijd.")}
      </p>

      {items.length === 0 && (
        <div className="border-2 border-dashed border-foreground/40 p-8 text-center mb-4" data-testid="invplan-empty">
          <div className="font-display uppercase font-bold text-lg">{t("Nog geen investeringen")}</div>
          <p className="text-muted-foreground text-sm mt-1">{t("Klik op '+ Investering toevoegen' om te beginnen.")}</p>
        </div>
      )}

      {showNpv && items.length >= 2 && (() => {
        // Rangschikking op NCW per euro alleen zinvol voor investeringen met een business case (opbrengsten of besparing).
        const kpis = items.map((it, idx) => ({ idx, it }))
          .filter(({ it }) => hasBusinessCase(it))
          .map(({ idx, it }) => ({ idx, it, kpi: computePlanKpi(it, { discount: scenarioDiscount, inflation: scenarioInflation, horizon: horizonYears }) })).filter((x) => x.kpi != null);
        if (kpis.length < 2) return null;
        const ranked = [...kpis].sort((a, b) => (b.kpi.npvPerEuro ?? -Infinity) - (a.kpi.npvPerEuro ?? -Infinity));
        const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
        return (
          <div className="mb-6 border-2 border-foreground bg-card heavy-shadow" data-testid="invplan-comparison">
            <div className="p-4 border-b-2 border-foreground flex items-center justify-between">
              <div>
                <div className="stat-label">{t("Rangschikking")}</div>
                <h3 className="font-display font-black text-lg uppercase tracking-tight">{t("Vergelijk investeringen")}</h3>
              </div>
              <div className="text-[10px] text-muted-foreground font-mono-num text-right">
                {t("Gesorteerd op netto contante waarde per € geïnvesteerd")}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-foreground text-background">
                    <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest">#</th>
                    <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Omschrijving")}</th>
                    <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Bedrag")}</th>
                    <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest" title={termHelp("npv")}>{termShort("npv")}</th>
                    <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest">{t("NCW / €")}</th>
                    <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest" title={termHelp("irr")}>{termShort("irr")}</th>
                    <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest" title={termHelp("payback")}>{termShort("payback")}</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((r, rank) => (
                    <tr key={r.idx} className={rank % 2 ? "bg-muted/40" : ""} data-testid={`invplan-comparison-row-${rank}`}>
                      <td className="px-3 py-2 font-mono-num font-bold">
                        <span className={`inline-flex items-center justify-center w-6 h-6 border-2 border-foreground text-xs ${rank === 0 ? "bg-positive text-background" : "bg-background"}`}>{rank + 1}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-display uppercase font-bold text-sm">{r.it.name || `${t("Plan")} #${String(r.idx + 1).padStart(2, "0")}`}</div>
                        <div className="text-[10px] text-muted-foreground font-mono-num">#{String(r.idx + 1).padStart(2, "0")}</div>
                      </td>
                      <td className="px-3 py-2 font-mono-num text-right">{fmt(r.kpi.amountInvested)}</td>
                      <td className={`px-3 py-2 font-mono-num text-right font-bold ${r.kpi.npv >= 0 ? "text-positive" : "text-destructive"}`}>{fmt(r.kpi.npv)}</td>
                      <td className={`px-3 py-2 font-mono-num text-right font-bold ${(r.kpi.npvPerEuro ?? 0) >= 0 ? "text-positive" : "text-destructive"}`}>
                        {r.kpi.npvPerEuro == null ? "—" : (r.kpi.npvPerEuro >= 0 ? "+" : "") + r.kpi.npvPerEuro.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 font-mono-num text-right">{r.kpi.irr == null ? "—" : `${(r.kpi.irr * 100).toFixed(1)}%`}</td>
                      <td className="px-3 py-2 font-mono-num text-right">{r.kpi.payback == null ? "—" : `${r.kpi.payback.toFixed(1)} ${t("jaar")}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      <div className="space-y-4">
        {items.map((it, idx) => (
          <div key={it._lid || `legacy-${idx}`} className="border-2 border-foreground p-4 bg-background heavy-shadow" data-testid={`invplan-row-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="stat-label">#{String(idx + 1).padStart(2, "0")}</div>
              <button type="button" onClick={() => remove(idx)} data-testid={`invplan-remove-${idx}`}
                className="text-sm font-display uppercase font-bold text-destructive hover:underline">{t("Verwijderen")}</button>
            </div>
            <div className="grid md:grid-cols-6 gap-3">
              <div className="md:col-span-2">
                <label className="stat-label">{t("Omschrijving")}</label>
                <input value={it.name} onChange={(e) => patch(idx, "name", e.target.value)}
                  data-testid={`invplan-name-${idx}`}
                  placeholder={t("bijv. Uitbreiding steiger")}
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="stat-label">{t("Bedrag (€)")}</label>
                <MoneyInput value={it.amount} onCommit={(n) => patch(idx, "amount", n)} testid={`invplan-amount-${idx}`} />
                {Number(it.expected_subsidy || 0) > 0 && Number(it.amount || 0) > 0 && (
                  <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`invplan-net-hint-${idx}`}>
                    {t("Netto na subsidie: € {n}").replace("{n}",
                      Math.max(0, Number(it.amount || 0) - Number(it.expected_subsidy || 0))
                        .toLocaleString("nl-NL", { maximumFractionDigits: 0 }))}
                  </div>
                )}
              </div>
              <div>
                <label className="stat-label">{t("Startjaar")}</label>
                <input type="number" step="1" value={it.start_year} onChange={(e) => patch(idx, "start_year", e.target.value)}
                  data-testid={`invplan-start_year-${idx}`}
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="stat-label">{t("Afschrijvingsjaren")}</label>
                <input type="number" step="1" value={it.depreciation_years} onChange={(e) => patch(idx, "depreciation_years", e.target.value)}
                  data-testid={`invplan-dep_years-${idx}`}
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              {club?.btw_regime === "gemengd" && (
                <div className="md:col-span-3">
                  <label className="stat-label">{t("BTW-aftrek op deze investering")}</label>
                  <select value={it.btw_aftrek || ""} onChange={(e) => patch(idx, "btw_aftrek", e.target.value || null)}
                    data-testid={`invplan-btw_aftrek-${idx}`}
                    className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                    {BTW_AFTREK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.label)}{o.value === "" ? ` · ${t(aftrekLabel(club?.btw_default_aftrek || "pro_rata"))}` : ""}</option>)}
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-1">{t("Het niet-aftrekbare deel van de BTW ({pct} % × niet-aftrekbaar aandeel) wordt bij het investeringsbedrag opgeteld en meegeschreven.").replace("{pct}", String(club?.btw_default_rate ?? 21))}</p>
                </div>
              )}
              <div className="md:col-span-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={Boolean(it.funded_from_reservation)}
                    onChange={(e) => patch(idx, "funded_from_reservation", e.target.checked)}
                    data-testid={`invplan-funded-${idx}`}
                    className="w-5 h-5 accent-blue-600" />
                  <span className="font-display uppercase font-bold text-sm">{t("Gefinancierd uit bestemmingsreserve")}</span>
                  <span className="text-xs text-muted-foreground">{t("Ten laste van de reserve: wel cash-uitstroom, geen activering, dus geen afschrijving.")}</span>
                </label>
                {Boolean(it.funded_from_reservation) && (() => {
                  const selectedName = it.reservation_name || "";
                  const selected = reserves.find((r) => r.name === selectedName);
                  const available = selected?.latest_closing_balance;
                  // Netto onttrekking uit reservering = bruto − subsidie (subsidie
                  // dekt een deel van de investering en hoeft NIET uit de reservering
                  // te komen). Zo blijft de reservering-check accuraat.
                  const grossAmt = Number(it.amount) || 0;
                  const subsidy = Math.max(0, Number(it.expected_subsidy) || 0);
                  const amt = Math.max(0, grossAmt - subsidy);
                  const overrun = selected && available != null && amt > (available + 0.005);
                  return (
                    <div className="mt-3 pl-7">
                      <label className="stat-label">{t("Selecteer reserve")}</label>
                      <select
                        value={selectedName}
                        onChange={(e) => patch(idx, "reservation_name", e.target.value)}
                        data-testid={`invplan-reservation-select-${idx}`}
                        className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                        <option value="">{t("— kies een reserve —")}</option>
                        {reserves.map((r) => {
                          const bal = r.latest_closing_balance;
                          const balLabel = bal != null
                            ? new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(bal)
                            : t("geen saldo bekend");
                          return (
                            <option key={r.id} value={r.name}>
                              [{r.category === "general" ? t("Alg") : t("Best")}] {r.name} — {balLabel}
                            </option>
                          );
                        })}
                      </select>
                      {selectedName && !selected && (
                        <div className="mt-2 border-2 border-destructive bg-destructive/10 p-2 text-xs text-destructive" data-testid={`invplan-reservation-missing-${idx}`}>
                          {t("De geselecteerde reserve bestaat niet meer — kies een andere.")}
                        </div>
                      )}
                      {overrun && (
                        <div className="mt-2 border-2 border-destructive bg-destructive/10 p-2 text-xs" data-testid={`invplan-reservation-overrun-${idx}`}>
                          <b>{t("Onvoldoende saldo")}:</b> {t("investering")} {new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amt)}{" "}
                          {t("overschrijdt beschikbaar saldo")} {new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(available)} ·{" "}
                          {t("Kies een andere reserve, verlaag het bedrag of dotereer eerst extra aan deze reserve.")}
                        </div>
                      )}
                      {selected && !overrun && available != null && (
                        <div className="mt-1 text-xs text-muted-foreground font-mono-num" data-testid={`invplan-reservation-available-${idx}`}>
                          {t("Beschikbaar saldo")}: <b className="text-positive">{new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(available)}</b>
                          {amt > 0 && (
                            <span> · {t("na deze investering")}: <b>{new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(available - amt)}</b></span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

            </div>
            <div className="mt-3 border-t border-foreground/20 pt-3" data-testid={`invplan-advanced-${idx}`}>
              <button type="button" onClick={() => toggleAdv(it, idx)} data-testid={`invplan-advanced-toggle-${idx}`} aria-expanded={isAdvOpen(it, idx)}
                className="inline-flex items-center gap-1.5 text-[11px] font-display uppercase font-bold tracking-widest text-muted-foreground hover:text-foreground">
                {isAdvOpen(it, idx) ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {t("Geavanceerd")} <span className="normal-case tracking-normal font-sans font-normal">· {t("subsidie, prijspeil, extra opbrengsten/kosten, eigen disconteringsvoet")}</span>
                {hasAdvValues(it) && !isAdvOpen(it, idx) && <span className="ml-1 px-1 py-px bg-primary text-primary-foreground text-[9px]">{t("ingevuld")}</span>}
              </button>
              {isAdvOpen(it, idx) && (
                <div className="grid md:grid-cols-6 gap-3 mt-3">
              <div>
                <label className="stat-label">{t("Verwachte subsidie (€)")}</label>
                <MoneyInput value={it.expected_subsidy} onCommit={(n) => patch(idx, "expected_subsidy", n)} testid={`invplan-subsidy-${idx}`} />
                <div className="text-[10px] text-muted-foreground mt-0.5">{t("Wordt afgetrokken vóór activering of onttrekking uit reservering")}</div>
              </div>
              <div>
                <label className="stat-label">{t("Prijspeil-jaar")}</label>
                <input type="number" step="1" value={it.money_year ?? ""} onChange={(e) => patch(idx, "money_year", e.target.value)}
                  data-testid={`invplan-money_year-${idx}`}
                  placeholder={String(it.start_year || startYear)}
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary" />
                <div className="text-[10px] text-muted-foreground mt-0.5">{t("Jaar waarin het bedrag is uitgedrukt")}</div>
              </div>
              <div>
                <label className="stat-label">{t("Kosten-inflatie")}</label>
                <PctInput
                  value={it.cost_inflation_rate}
                  onChange={(v) => patch(idx, "cost_inflation_rate", v)}
                  testid={`invplan-cost_inflation-${idx}`}
                  placeholder={`${(Number(scenarioInflation || 0) * 100 || 2).toFixed(1)}`}
                />
                <div className="text-[10px] text-muted-foreground mt-0.5">{t("Leeg = scenario-inflatie")}</div>
              </div>
              <div>
                <label className="stat-label">{t("Extra opbrengsten / jaar")}</label>
                <MoneyInput value={it.additional_revenue ?? 0} onCommit={(n) => patch(idx, "additional_revenue", n)} testid={`invplan-add_revenue-${idx}`} />
              </div>
              <div>
                <label className="stat-label">{t("Extra kosten / jaar")}</label>
                <MoneyInput value={it.additional_costs ?? 0} onCommit={(n) => patch(idx, "additional_costs", n)} testid={`invplan-add_costs-${idx}`} />
              </div>
              <div>
                <label className="stat-label">{t("Groei extra opbrengsten")}</label>
                <PctInput
                  value={it.additional_revenue_growth}
                  onChange={(v) => patch(idx, "additional_revenue_growth", v)}
                  testid={`invplan-add_rev_growth-${idx}`}
                  placeholder={t("Leeg = inflatie")}
                />
              </div>
              <div>
                <label className="stat-label">{t("Groei extra kosten")}</label>
                <PctInput
                  value={it.additional_costs_growth}
                  onChange={(v) => patch(idx, "additional_costs_growth", v)}
                  testid={`invplan-add_cost_growth-${idx}`}
                  placeholder={t("Leeg = inflatie")}
                />
              </div>
              {hasNpvAccess && (
                <div>
                  <label className="stat-label inline-flex items-center gap-2">
                    {t("Disconteringsvoet")}
                    <HelpButton section="npv" label="Uitleg disconteringsvoet" size="sm" />
                  </label>
                  <PctInput
                    value={it.discount_rate}
                    onChange={(v) => patch(idx, "discount_rate", v)}
                    testid={`invplan-discount_rate-${idx}`}
                    placeholder={`${(Number(scenarioDiscount) * 100).toFixed(1)}`}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid={`invplan-discount-hint-${idx}`}>
                    {t("Rekenrente voor NCW en terugverdientijd van dít plan. Leeg = scenario-waarde ({pct} %). Alleen relevant als het plan opbrengsten of een besparing heeft.").replace("{pct}", (Number(scenarioDiscount) * 100).toFixed(1).replace(".", ","))}
                  </p>
                </div>
              )}
                </div>
              )}
            </div>
            {(() => {
              const my = Number(it.money_year || 0) || (Number(it.start_year) || 0);
              const sy = Number(it.start_year) || 0;
              const ci = it.cost_inflation_rate === "" || it.cost_inflation_rate == null ? Number(scenarioInflation || 0) : Number(it.cost_inflation_rate);
              const years = Math.max(0, sy - my);
              const infl_factor = Math.pow(1 + (ci || 0), years);
              const bruto = Number(it.amount || 0) * infl_factor;
              const subs = Math.max(0, Number(it.expected_subsidy || 0)) * infl_factor;
              const netto = Math.max(0, bruto - subs);
              if (years <= 0 || Number(it.amount || 0) <= 0) return null;
              const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
              return (
                <div className="mt-2 text-xs text-muted-foreground font-mono-num" data-testid={`invplan-inflated-${idx}`}>
                  {t("Bedrag na {n} jaar kosten-inflatie ({rate}%)").replace("{n}", years).replace("{rate}", ((ci || 0) * 100).toFixed(2))}: <span className="font-bold text-foreground">{fmt(bruto)}</span>
                  {subs > 0 && (
                    <>
                      {" · "}
                      {t("Netto na subsidie")}: <span className="font-bold text-foreground" data-testid={`invplan-inflated-net-${idx}`}>{fmt(netto)}</span>
                    </>
                  )}
                </div>
              );
            })()}
            {Number(it.amount) > 0 && Number(it.depreciation_years) > 0 && !it.funded_from_reservation && (() => {
              // Consistent met backend compute: netto na cost-inflation én subsidie.
              // Voorheen deelde deze hint alleen `amount / life` — dat gaf een
              // te lage afschrijving zodra er cost-inflation of subsidie speelde.
              const my = Number(it.money_year || 0) || (Number(it.start_year) || 0);
              const sy = Number(it.start_year) || 0;
              const ci = it.cost_inflation_rate === "" || it.cost_inflation_rate == null
                ? Number(scenarioInflation || 0)
                : Number(it.cost_inflation_rate);
              const infl = Math.pow(1 + (ci || 0), Math.max(0, sy - my));
              const bruto = Number(it.amount || 0) * infl;
              const subs = Math.max(0, Number(it.expected_subsidy || 0)) * infl;
              const netto = Math.max(0, bruto - subs);
              const perYear = netto / Number(it.depreciation_years);
              const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
              return (
                <div className="mt-3 text-xs text-muted-foreground font-mono-num" data-testid={`invplan-dep-preview-${idx}`}>
                  {t("Jaarlijkse afschrijving")}: <span className="font-bold text-foreground">{fmt(perYear)}</span>
                  {" · "}{Number(it.depreciation_years)} {t("jaar")} · {t("start")} {Number(it.start_year) + 1}
                  {(bruto !== Number(it.amount) || subs > 0) && (
                    <span className="ml-1 text-[10px]">({t("op basis van netto")} {fmt(netto)})</span>
                  )}
                </div>
              );
            })()}
            {Number(it.amount) > 0 && it.funded_from_reservation && (
              <div className="mt-3 text-xs text-muted-foreground font-mono-num" data-testid={`invplan-reserve-note-${idx}`}>
                {t("Ten laste van reserve — geen afschrijving")}
              </div>
            )}
            {(Number(it.additional_revenue) !== 0 || Number(it.additional_costs) !== 0) && (
              <div className="mt-2 text-xs text-muted-foreground font-mono-num" data-testid={`invplan-add-result-${idx}`}>
                {t("Netto additioneel bedrijfsresultaat")}: <span className={`font-bold ${(Number(it.additional_revenue) - Number(it.additional_costs)) >= 0 ? "text-positive" : "text-destructive"}`}>
                  {new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(it.additional_revenue) - Number(it.additional_costs))}
                </span> / {t("jaar vanaf")} {Number(it.start_year)}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-foreground/20">
              <div className="stat-label mb-2">{t("Financiering met lening (optioneel)")}</div>
              <div className="grid md:grid-cols-4 gap-3">
                <div>
                  <label className="stat-label">{t("Leningsbedrag (€)")}</label>
                  <MoneyInput value={it.loan_amount ?? 0} onCommit={(n) => patch(idx, "loan_amount", n)} testid={`invplan-loan_amount-${idx}`} />
                </div>
                <div>
                  <label className="stat-label">{t("Rente")}</label>
                  <PctInput
                    value={it.loan_interest_rate ?? 0.05}
                    onChange={(v) => patch(idx, "loan_interest_rate", v)}
                    testid={`invplan-loan_rate-${idx}`}
                  />
                </div>
                <div>
                  <label className="stat-label">{t("Termijn (jaren)")}</label>
                  <input type="number" step="1" value={it.loan_term_years ?? 15} onChange={(e) => patch(idx, "loan_term_years", e.target.value)}
                    data-testid={`invplan-loan_term-${idx}`}
                    className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="stat-label">{t("Aflossingsstructuur")}</label>
                  <select value={it.loan_type ?? "annuity"} onChange={(e) => patch(idx, "loan_type", e.target.value)}
                    data-testid={`invplan-loan_type-${idx}`}
                    className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="annuity">{t("Annuïteit")}</option>
                    <option value="linear">{t("Lineair")}</option>
                  </select>
                </div>
              </div>
              {Number(it.loan_amount) > 0 && (
                <div className="mt-2 text-xs text-muted-foreground" data-testid={`invplan-loan-preview-${idx}`}>
                  {t("Lening wordt uitgekeerd in")} <span className="font-bold text-foreground">{Number(it.start_year)}</span> {t("en volledig afgelost in")} <span className="font-bold text-foreground">{Number(it.start_year) + Number(it.loan_term_years || 0)}</span>
                </div>
              )}
            </div>
            {(() => {
              if (!showNpv) return null;
              const kpi = computePlanKpi(it, { discount: scenarioDiscount, inflation: scenarioInflation, horizon: horizonYears });
              if (!kpi) return null;
              const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
              const npvColor = kpi.npv >= 0 ? "text-positive" : "text-destructive";
              // Zonder opbrengsten of besparing is er geen business case: NCW/IRR/terugverdientijd zeggen dan niets.
              const hasRevenue = hasBusinessCase(it);
              return (
                <div className="mt-4 border-2 border-foreground bg-muted/40 p-4" data-testid={`invplan-kpi-${idx}`}>
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <div className="stat-label inline-flex items-center gap-2">{t("Rendementscijfers")} <HelpButton section="npv" label="Uitleg netto contante waarde en disconteringsvoet" size="sm" /></div>
                    <div className="text-[10px] text-muted-foreground font-mono-num">
                      {kpi.usingPlanDiscount
                        ? t("Plan-disconteringsvoet")
                        : t("Fallback: scenario")}
                      {" · "}
                      {(kpi.discount * 100).toFixed(2)}%
                    </div>
                  </div>
                  {!hasRevenue && (
                    <div className="mb-3 border-2 border-dashed border-foreground/40 bg-background p-3 text-sm" data-testid={`invplan-kpi-no-revenue-${idx}`}>
                      <span className="font-bold">{t("Geen opbrengsten of besparing ingevuld")}</span>{" — "}
                      {t("deze investering verdient zichzelf niet terug (bijv. clubhuisverbouwing). Netto contante waarde, intern rendement en terugverdientijd zijn dan niet betekenisvol en worden niet getoond. Levert de investering wél iets op (zonnepanelen, extra steiger)? Vul onder Geavanceerd 'Extra opbrengsten / jaar' in, of een negatief bedrag bij 'Extra kosten / jaar' voor een besparing.")}
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                    {hasRevenue && (<>
                    <div>
                      <div className="stat-label" title={termHelp("npv")}>{termShort("npv")}</div>
                      <div className={`font-mono-num font-bold text-lg ${npvColor}`} data-testid={`invplan-kpi-npv-${idx}`}>{fmt(kpi.npv)}</div>
                    </div>
                    <div>
                      <div className="stat-label" title={termHelp("irr")}>{termShort("irr")}</div>
                      <div className="font-mono-num font-bold text-lg" data-testid={`invplan-kpi-irr-${idx}`}>
                        {kpi.irr == null ? "—" : `${(kpi.irr * 100).toFixed(1)}%`}
                      </div>
                    </div>
                    <div>
                      <div className="stat-label">{t("Terugverdientijd")}</div>
                      <div className="font-mono-num font-bold text-lg" data-testid={`invplan-kpi-payback-${idx}`}>
                        {kpi.payback == null ? "—" : `${kpi.payback.toFixed(1)} ${t("jaar")}`}
                      </div>
                    </div>
                    <div>
                      <div className="stat-label" title={term("dpayback")}>{termShort("dpayback")}</div>
                      <div className="font-mono-num font-bold text-lg" data-testid={`invplan-kpi-dpayback-${idx}`}>
                        {kpi.dpayback == null ? "—" : `${kpi.dpayback.toFixed(1)} ${t("jaar")}`}
                      </div>
                    </div>
                    </>)}
                    <div>
                      <div className="stat-label">{t("Eigen inbreng Y0")}</div>
                      <div className={`font-mono-num font-bold text-lg ${kpi.y0Cash < 0 ? "text-destructive" : "text-foreground"}`} data-testid={`invplan-kpi-equity-${idx}`}>{fmt(kpi.y0Cash)}</div>
                    </div>
                    <div>
                      <div className="stat-label">{t("Totaal over horizon")}</div>
                      <div className={`font-mono-num font-bold text-lg ${kpi.totalCashOverHorizon >= 0 ? "text-positive" : "text-destructive"}`} data-testid={`invplan-kpi-total-${idx}`}>{fmt(kpi.totalCashOverHorizon)}</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-3 font-mono-num">
                    {t("Indicatieve berekening per investering, exclusief belastingeffect en afschrijvingen. Horizon = {h} jaar.").replace("{h}", String(kpi.horizon))}
                  </div>
                </div>
              );
            })()}
          </div>
        ))}
      </div>

      <button type="button" onClick={add} data-testid="invplan-add"
        className="mt-4 btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2">
        + {t("Investering toevoegen")}
      </button>
    </div>
  );
}


function ReserveDotationsStep({ t, form, setForm, startYear, horizonYears, scenarioInflation, reserves = [] }) {
  const items = form.reserve_dotations || [];
  const horizonEnd = startYear + horizonYears;
  const add = () => setForm((f) => ({
    ...f,
    reserve_dotations: [
      ...(f.reserve_dotations || []),
      { _lid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())), reserve_name: reserves[0]?.name || "", amount_per_year: 0, growth_rate: null, start_year: startYear, end_year: horizonEnd },
    ],
  }));
  const remove = (idx) => setForm((f) => ({
    ...f,
    reserve_dotations: (f.reserve_dotations || []).filter((_, i) => i !== idx),
  }));
  const patch = (idx, k, v) => setForm((f) => ({
    ...f,
    reserve_dotations: (f.reserve_dotations || []).map((it, i) => (i === idx ? { ...it, [k]: v } : it)),
  }));
  const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

  // Preview the closing balance per reserve at the horizon end, given the current dotations
  // and the reserve's most-recently-known closing_balance from historicals.
  const preview = reserves.map((r) => {
    const baseline = Number(r.latest_closing_balance || 0);
    const rows = items.filter((d) => d.reserve_name === r.name);
    let total = 0;
    for (const d of rows) {
      const amt = Number(d.amount_per_year || 0);
      if (!amt) continue;
      const gRaw = d.growth_rate;
      const g = gRaw === null || gRaw === undefined || gRaw === "" ? Number(scenarioInflation || 0) : Number(gRaw);
      const ds = Number(d.start_year || startYear);
      const de = Number(d.end_year || horizonEnd);
      const years = Math.max(0, Math.min(de, horizonEnd) - Math.max(ds, startYear) + 1);
      for (let i = 0; i < years; i++) total += amt * Math.pow(1 + g, i);
    }
    return { name: r.name, category: r.category, baseline, total, ending: baseline + total };
  });

  return (
    <div>
      <div className="mb-2">
        <div className="stat-label">{t("Reserve-dotaties")}</div>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Periodieke dotaties per reserve")}</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        {t("Voeg jaarlijkse stortingen toe aan een reserve gedurende (een deel van) de scenario-horizon. Dit is een puur balans-projectie: de reserve groeit ermee mee, maar er is geen extra kasuitstroom in het scenario.")}
      </p>

      {reserves.length === 0 && (
        <div className="border-2 border-dashed border-foreground/40 p-8 text-center mb-4" data-testid="reserve-dotations-no-reserves">
          <div className="font-display uppercase font-bold text-lg">{t("Geen reserves op deze club")}</div>
          <p className="text-muted-foreground text-sm mt-1">
            {t("Definieer eerst één of meer reserves in de club-pagina (Bestemmingsreserves) voordat je hier dotaties kunt plannen.")}
          </p>
        </div>
      )}

      {reserves.length > 0 && items.length === 0 && (
        <div className="border-2 border-dashed border-foreground/40 p-8 text-center mb-4" data-testid="reserve-dotations-empty">
          <div className="font-display uppercase font-bold text-lg">{t("Nog geen dotaties")}</div>
          <p className="text-muted-foreground text-sm mt-1">{t("Klik op '+ Dotatie toevoegen' om te beginnen.")}</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((it, idx) => (
          <div key={it._lid || `legacy-${idx}`} className="border-2 border-foreground bg-card p-4" data-testid={`reserve-dotation-${idx}`}>
            <div className="grid md:grid-cols-6 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="stat-label">{t("Reserve")}</label>
                <select
                  value={it.reserve_name || ""}
                  onChange={(e) => patch(idx, "reserve_name", e.target.value)}
                  data-testid={`reserve-dotation-name-${idx}`}
                  className="w-full mt-1 border-2 border-foreground bg-background px-2 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="" disabled>{t("Kies een reserve…")}</option>
                  {reserves.map((r) => (
                    <option key={r.id || r.name} value={r.name}>
                      {(r.category === "general" ? "Algemene reserve" : r.name) + " — " + fmt(Number(r.latest_closing_balance || 0))}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="stat-label">{t("Bedrag / jaar")}</label>
                <MoneyInput
                  value={it.amount_per_year || 0}
                  onCommit={(v) => patch(idx, "amount_per_year", Number(v) || 0)}
                  testid={`reserve-dotation-amount-${idx}`}
                  className="w-full mt-1 border-2 border-foreground bg-background px-2 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="stat-label">{t("Groeivoet")}</label>
                <PctInput
                  value={it.growth_rate}
                  onChange={(v) => patch(idx, "growth_rate", v === "" ? null : v)}
                  testid={`reserve-dotation-growth-${idx}`}
                  placeholder={`${((Number(scenarioInflation) || 0) * 100).toFixed(1)}`}
                  className="w-full border-2 border-foreground bg-background px-2 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <div className="text-[10px] text-muted-foreground mt-1">{t("Leeg = scenario-inflatie")}</div>
              </div>
              <div>
                <label className="stat-label">{t("Vanaf jaar")}</label>
                <input
                  type="number"
                  value={it.start_year ?? startYear}
                  onChange={(e) => patch(idx, "start_year", Number(e.target.value) || startYear)}
                  data-testid={`reserve-dotation-start-${idx}`}
                  className="w-full mt-1 border-2 border-foreground bg-background px-2 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="stat-label">{t("T/m jaar")}</label>
                <input
                  type="number"
                  value={it.end_year ?? horizonEnd}
                  onChange={(e) => patch(idx, "end_year", Number(e.target.value) || horizonEnd)}
                  data-testid={`reserve-dotation-end-${idx}`}
                  className="w-full mt-1 border-2 border-foreground bg-background px-2 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button
                type="button"
                onClick={() => remove(idx)}
                data-testid={`reserve-dotation-remove-${idx}`}
                className="btn-brutal bg-background text-destructive border-2 border-destructive px-3 py-1 font-display uppercase text-xs"
              >
                {t("Verwijderen")}
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        disabled={reserves.length === 0}
        data-testid="reserve-dotation-add"
        className="mt-4 btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        + {t("Dotatie toevoegen")}
      </button>

      {items.length > 0 && (
        <div className="mt-6 border-2 border-foreground bg-muted/40 p-4" data-testid="reserve-dotation-preview">
          <div className="stat-label mb-2">{t("Preview eindsaldo per reserve (excl. onttrekkingen)")}</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="py-1 pr-3 font-display uppercase text-xs tracking-widest">{t("Reserve")}</th>
                <th className="py-1 pr-3 font-display uppercase text-xs tracking-widest text-right">{t("Beginsaldo")}</th>
                <th className="py-1 pr-3 font-display uppercase text-xs tracking-widest text-right">{t("Totale dotaties")}</th>
                <th className="py-1 font-display uppercase text-xs tracking-widest text-right">{t("Eindsaldo Y{h}").replace("{h}", String(horizonYears))}</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((p) => (
                <tr key={p.name} className="border-t border-border">
                  <td className="py-2 pr-3">{p.category === "general" ? "Algemene reserve" : p.name}</td>
                  <td className="py-2 pr-3 font-mono-num text-right">{fmt(p.baseline)}</td>
                  <td className="py-2 pr-3 font-mono-num text-right text-positive">{p.total > 0 ? `+ ${fmt(p.total)}` : fmt(0)}</td>
                  <td className="py-2 font-mono-num text-right font-bold">{fmt(p.ending)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-muted-foreground mt-2">
            {t("Preview houdt geen rekening met onttrekkingen uit investeringen. De volledige projectie zie je op het scenariorapport.")}
          </div>
        </div>
      )}
    </div>
  );
}
